import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardActions,
  Typography,
  Grid,
  Avatar,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  IconButton,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Divider,
  Alert,
  Tooltip,
  Switch,
} from '@mui/material';
import {
  Phone,
  Email,
  AdminPanelSettings,
  CheckCircle,
  Cancel,
  Edit,
  Delete,
  Add,
  Person,
  Payment,
  Warning,
  EmojiEvents,
  AccountBalance,
  Security,
  PersonRemove,
  CurrencyRupee,
  Group,
} from '@mui/icons-material';
import axios from 'axios';
import toast from 'react-hot-toast';
import dayjs from 'dayjs';
import { useAuth } from '../contexts/AuthContext';
import { ACCENTS, tint, colorForName } from '../theme/accents';
import EmptyState from '../components/EmptyState';
import PageHeader from '../components/PageHeader';
import MemberDetailDialog from '../components/MemberDetailDialog';
import StatCard from '../components/StatCard';
import { formatINR } from '../utils/currency';

const Members = () => {
  const { user: currentUser, isAdmin } = useAuth();
  const [members, setMembers] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);
  const [detailMemberId, setDetailMemberId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [roleConfirmDialogOpen, setRoleConfirmDialogOpen] = useState(false);
  const [roleChangeTarget, setRoleChangeTarget] = useState(null);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    isActive: true,
    contributionMultiplier: 1,
  });
  const [createFormData, setCreateFormData] = useState({
    username: '',
    fullName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    isAdmin: false,
    contributionMultiplier: 1,
  });
  const [baseAmount, setBaseAmount] = useState(null);
  const [newPassword, setNewPassword] = useState('');

  useEffect(() => {
    fetchMembers();
    fetchBaseAmount();
  }, []);

  // The base share is an admin setting; used to show what a multiplier works out to
  const fetchBaseAmount = async () => {
    try {
      const response = await axios.get('/api/admin/settings');
      const value = Number(response.data?.monthlyAmount);
      if (Number.isFinite(value)) {
        setBaseAmount(value);
      }
    } catch (error) {
      // Non-admins cannot read settings - the multiplier still displays fine
    }
  };

  const fetchMembers = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/users');
      const enrichedMembers = await Promise.all(
        response.data.map(async (member) => {
          try {
            const [paymentsRes, penaltiesRes] = await Promise.all([
              axios.get(`/api/payments/user/${member.id}`),
              axios.get(`/api/penalties/user/${member.id}`),
            ]);
            
            const currentMonth = dayjs().format('YYYY-MM');
            const currentMonthPayment = paymentsRes.data.find(
              p => p.payment_month === currentMonth
            );
            
            const pendingPenalties = penaltiesRes.data.filter(
              p => p.status === 'pending'
            );
            
            return {
              ...member,
              currentMonthStatus: currentMonthPayment?.status || 'pending',
              totalPayments: paymentsRes.data.length,
              pendingPenalties: pendingPenalties.length,
              pendingPenaltyAmount: pendingPenalties.reduce(
                (sum, p) => sum + parseFloat(p.amount), 0
              ),
            };
          } catch (error) {
            return {
              ...member,
              currentMonthStatus: 'pending',
              totalPayments: 0,
              pendingPenalties: 0,
              pendingPenaltyAmount: 0,
            };
          }
        })
      );
      setMembers(enrichedMembers);
    } catch (error) {
      toast.error('Failed to fetch members');
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (member) => {
    setSelectedMember(member);
    setFormData({
      fullName: member.full_name,
      email: member.email || '',
      phone: member.phone || '',
      isActive: Boolean(member.is_active),
      contributionMultiplier: Number(member.contribution_multiplier ?? 1),
    });
    setEditDialogOpen(true);
  };

  const handlePasswordResetClick = (member) => {
    setSelectedMember(member);
    setNewPassword('');
    setPasswordDialogOpen(true);
  };

  const handleEditSubmit = async () => {
    try {
      await axios.put(`/api/users/${selectedMember.id}`, formData);
      toast.success('Member updated successfully');
      setEditDialogOpen(false);
      fetchMembers();
    } catch (error) {
      toast.error('Failed to update member');
    }
  };

  const handlePasswordReset = async () => {
    try {
      await axios.put(`/api/users/${selectedMember.id}/reset-password`, {
        newPassword,
      });
      toast.success('Password reset successfully');
      setPasswordDialogOpen(false);
    } catch (error) {
      toast.error('Failed to reset password');
    }
  };

  const handleDeleteMember = async (member) => {
    if (window.confirm(`Are you sure you want to delete ${member.full_name}? This action cannot be undone and will remove all associated data.`)) {
      try {
        await axios.delete(`/api/users/${member.id}`);
        toast.success('Member deleted successfully');
        fetchMembers();
      } catch (error) {
        toast.error(error.response?.data?.error || 'Failed to delete member');
      }
    }
  };

  const handleRoleToggleClick = (member) => {
    if (member.id === currentUser.id) {
      toast.error('You cannot change your own role');
      return;
    }
    setRoleChangeTarget(member);
    setRoleConfirmDialogOpen(true);
  };

  const handleRoleToggle = async () => {
    try {
      const newRole = !roleChangeTarget.is_admin;
      await axios.put(`/api/users/${roleChangeTarget.id}/role`, {
        isAdmin: newRole
      });
      
      const action = newRole ? 'promoted to admin' : 'demoted to member';
      toast.success(`${roleChangeTarget.full_name} has been ${action}`);
      setRoleConfirmDialogOpen(false);
      setRoleChangeTarget(null);
      fetchMembers();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update user role');
    }
  };

  const handleCreateMember = async () => {
    if (createFormData.password !== createFormData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    try {
      await axios.post('/api/users', {
        username: createFormData.username,
        password: createFormData.password,
        fullName: createFormData.fullName,
        email: createFormData.email,
        phone: createFormData.phone,
        isAdmin: createFormData.isAdmin,
        contributionMultiplier: createFormData.contributionMultiplier,
      });
      toast.success('Member created successfully');
      setCreateDialogOpen(false);
      setCreateFormData({
        username: '',
        fullName: '',
        email: '',
        phone: '',
        password: '',
        confirmPassword: '',
        isAdmin: false,
        contributionMultiplier: 1,
      });
      fetchMembers();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to create member');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'pending':
        return 'warning';
      case 'overdue':
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <Box>
      <PageHeader
        eyebrow="The circle"
        title="Members"
        subtitle={baseAmount ? `Base share ${formatINR(baseAmount)} per month` : undefined}
        accent={ACCENTS.indigo}
        actions={
          isAdmin && (
            <Button variant="contained" startIcon={<Add />} onClick={() => setCreateDialogOpen(true)}>
              Add Member
            </Button>
          )
        }
      />

      {/* Summary Stats */}
      <Grid container spacing={2.5} mb={3}>
        <Grid item xs={6} md={3}>
          <StatCard
            title="Members"
            value={members.filter(m => !m.is_system_admin).length}
            subtitle="In the circle"
            icon={<Person />}
            accent={ACCENTS.indigo}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            title="Active"
            value={members.filter(m => m.is_active && !m.is_system_admin).length}
            subtitle="Contributing"
            icon={<CheckCircle />}
            accent={ACCENTS.emerald}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            title="Paid"
            value={members.filter(m => m.currentMonthStatus === 'completed' && !m.is_system_admin).length}
            subtitle={dayjs().format('MMMM')}
            icon={<Payment />}
            accent={ACCENTS.marigold}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            title="Penalised"
            value={members.filter(m => m.pendingPenalties > 0 && !m.is_system_admin).length}
            subtitle="Outstanding"
            icon={<Warning />}
            accent={ACCENTS.vermilion}
          />
        </Grid>
      </Grid>

      {/* Members Grid */}
      <Grid container spacing={3}>
        {members.filter(member => !member.is_system_admin).length === 0 && !loading && (
          <Grid item xs={12}>
            <EmptyState
              icon={<Group />}
              accent={ACCENTS.indigo}
              title="No members yet"
              description="Add the people in the group and their share counts, and their monthly dues start being tracked."
            />
          </Grid>
        )}
        {members.filter(member => !member.is_system_admin).map((member) => (
          <Grid item xs={12} sm={6} md={4} lg={3} xl={2.4} key={member.id}>
            <Card 
              sx={{
                height: '100%',
                opacity: member.is_active ? 1 : 0.7,
                borderColor: tint(colorForName(member.full_name), 0.35),
                background: `linear-gradient(155deg, ${tint(colorForName(member.full_name), 0.14)} 0%, rgba(0,0,0,0) 60%)`,
                '&:hover': {
                  transform: 'translateY(-4px)',
                  transition: 'transform 0.3s',
                  borderColor: tint(colorForName(member.full_name), 0.65),
                },
              }}
            >
              <CardContent sx={{ pb: 1.5 }}>
                {/* Identity */}
                <Box display="flex" alignItems="center" gap={1.75} mb={2.5}>
                  <Avatar
                    sx={{
                      // Each member keeps one identity colour across the app
                      bgcolor: tint(colorForName(member.full_name), 0.2),
                      color: colorForName(member.full_name),
                      border: '2px solid',
                      borderColor: tint(colorForName(member.full_name), 0.55),
                      fontWeight: 700,
                      fontSize: 18,
                      width: 46,
                      height: 46,
                    }}
                  >
                    {member.full_name.charAt(0)}
                  </Avatar>
                  <Box flexGrow={1} sx={{ minWidth: 0 }}>
                    <Typography variant="subtitle1" noWrap sx={{ fontWeight: 700, lineHeight: 1.3 }}>
                      {member.full_name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap display="block">
                      @{member.username}
                    </Typography>
                  </Box>
                  {Boolean(member.is_admin) && (
                    <Chip icon={<AdminPanelSettings />} label="Admin" color="warning" size="small" />
                  )}
                </Box>

                {/* What they contribute - the fact this card exists to show */}
                {!member.is_system_admin && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'baseline',
                      justifyContent: 'space-between',
                      gap: 1,
                      p: 1.75,
                      mb: 2,
                      borderRadius: 2,
                      bgcolor: tint(colorForName(member.full_name), 0.09),
                      border: '1px solid',
                      borderColor: tint(colorForName(member.full_name), 0.22),
                    }}
                  >
                    <Box>
                      <Typography
                        className="tabular"
                        sx={{
                          fontFamily: '"Fraunces", Georgia, serif',
                          fontSize: '1.6rem',
                          fontWeight: 600,
                          lineHeight: 1.1,
                          color: colorForName(member.full_name),
                        }}
                      >
                        {baseAmount
                          ? formatINR(baseAmount * Number(member.contribution_multiplier ?? 1))
                          : `${Number(member.contribution_multiplier ?? 1)}×`}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        every month
                      </Typography>
                    </Box>
                    <Chip
                      size="small"
                      label={`${Number(member.contribution_multiplier ?? 1)}× share`}
                      variant="outlined"
                      sx={{ borderColor: 'divider', color: 'text.secondary' }}
                    />
                  </Box>
                )}

                {/* Status - sized to its content, not stretched across the card */}
                <Box display="flex" flexWrap="wrap" gap={0.75} mb={2}>
                  <Chip
                    size="small"
                    icon={member.currentMonthStatus === 'completed' ? <CheckCircle /> : undefined}
                    label={`${dayjs().format('MMM')} ${member.currentMonthStatus === 'completed' ? 'paid' : 'due'}`}
                    sx={{
                      bgcolor: tint(member.currentMonthStatus === 'completed' ? ACCENTS.emerald : ACCENTS.vermilion, 0.16),
                      color: member.currentMonthStatus === 'completed' ? ACCENTS.emerald : ACCENTS.vermilion,
                      '& .MuiChip-icon': { color: 'inherit' },
                    }}
                  />
                  <Chip
                    size="small"
                    label={member.is_active ? 'Active' : 'Inactive'}
                    variant="outlined"
                    sx={{ borderColor: 'divider', color: 'text.secondary' }}
                  />
                  {member.pendingPenalties > 0 && (
                    <Chip
                      size="small"
                      icon={<Warning />}
                      label={`${formatINR(member.pendingPenaltyAmount)} penalty`}
                      sx={{
                        bgcolor: tint(ACCENTS.marigold, 0.16),
                        color: ACCENTS.marigold,
                        '& .MuiChip-icon': { color: 'inherit' },
                      }}
                    />
                  )}
                </Box>

                <Divider sx={{ mb: 1.5 }} />

                {/* Contact details are reference, so they read quietly */}
                <Box display="flex" alignItems="center" gap={1} mb={0.75}>
                  <Email sx={{ fontSize: 15, color: 'text.secondary' }} />
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {member.email || 'No email'}
                  </Typography>
                </Box>
                <Tooltip title={member.phone ? '' : 'WhatsApp reminders skip members without a phone number'}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Phone sx={{ fontSize: 15, color: member.phone ? 'text.secondary' : ACCENTS.marigold }} />
                    <Typography
                      variant="caption"
                      noWrap
                      sx={{ color: member.phone ? 'text.secondary' : ACCENTS.marigold }}
                    >
                      {member.phone || 'No phone number'}
                    </Typography>
                  </Box>
                </Tooltip>
              </CardContent>
              {/* Primary action left, admin tools right - not all crowded together */}
              <CardActions sx={{ px: 2, pb: 2, pt: 0, justifyContent: 'space-between' }}>
                <Button
                  size="small"
                  onClick={() => setDetailMemberId(member.id)}
                >
                  View details
                </Button>
                {isAdmin && (
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <IconButton 
                      size="small"
                      onClick={() => handleEditClick(member)}
                      title="Edit Member"
                    >
                      <Edit fontSize="small" />
                    </IconButton>
                    <IconButton 
                      size="small"
                      onClick={() => handleRoleToggleClick(member)}
                      title={member.is_admin ? "Remove Admin Role" : "Make Admin"}
                      color={member.is_admin ? "warning" : "primary"}
                      disabled={member.id === currentUser.id}
                    >
                      {member.is_admin ? <PersonRemove fontSize="small" /> : <Security fontSize="small" />}
                    </IconButton>
                    <IconButton 
                      size="small"
                      onClick={() => handleDeleteMember(member)}
                      title="Delete Member"
                      color="error"
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Box>
                )}
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Edit Member Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Member</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Full Name"
                value={formData.fullName}
                onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="number"
                label="Contribution multiplier"
                value={formData.contributionMultiplier}
                onChange={(e) => setFormData({ ...formData, contributionMultiplier: e.target.value })}
                inputProps={{ min: 0, step: 0.5 }}
                helperText={
                  baseAmount
                    ? `Pays ${formatINR(baseAmount * (Number(formData.contributionMultiplier) || 0))} per month (${formatINR(baseAmount)} base × ${formData.contributionMultiplier || 0})`
                    : 'Shares of the base monthly amount'
                }
              />
            </Grid>
            <Grid item xs={12}>
              <Box
                sx={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  gap: 2, p: 1.75, borderRadius: 2, border: '1px solid', borderColor: 'divider',
                  bgcolor: (t) => tint(formData.isActive ? ACCENTS.emerald : ACCENTS.silver, 0.08),
                }}
              >
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {formData.isActive ? 'Active member' : 'Inactive'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formData.isActive
                      ? 'Counts towards monthly collection and the distribution rotation'
                      : 'Stops new dues and leaves the rotation — anything already owed still stands'}
                  </Typography>
                </Box>
                <Switch
                  checked={Boolean(formData.isActive)}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  inputProps={{ 'aria-label': 'Member is active' }}
                />
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleEditSubmit} variant="contained">
            Update
          </Button>
          <Button 
            onClick={() => handlePasswordResetClick(selectedMember)} 
            color="warning"
          >
            Reset Password
          </Button>
        </DialogActions>
      </Dialog>

      {/* Password Reset Dialog */}
      <Dialog open={passwordDialogOpen} onClose={() => setPasswordDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reset Password for {selectedMember?.full_name}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="New Password"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            sx={{ mt: 2 }}
            helperText="Minimum 6 characters"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPasswordDialogOpen(false)}>Cancel</Button>
          <Button onClick={handlePasswordReset} variant="contained">
            Reset Password
          </Button>
        </DialogActions>
      </Dialog>

      {/* The shared member ledger. This page used to carry its own shorter
          copy: five rows per section, and one generic icon for every payment
          regardless of how it was actually paid. */}
      <MemberDetailDialog
        memberId={detailMemberId}
        open={Boolean(detailMemberId)}
        onClose={() => setDetailMemberId(null)}
      />

      {/* Create Member Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Member</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Username"
                value={createFormData.username}
                onChange={(e) => setCreateFormData({ ...createFormData, username: e.target.value })}
                required
                helperText="Unique username for login"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Full Name"
                value={createFormData.fullName}
                onChange={(e) => setCreateFormData({ ...createFormData, fullName: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={createFormData.email}
                onChange={(e) => setCreateFormData({ ...createFormData, email: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Phone"
                value={createFormData.phone}
                onChange={(e) => setCreateFormData({ ...createFormData, phone: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="number"
                label="Contribution multiplier"
                value={createFormData.contributionMultiplier}
                onChange={(e) => setCreateFormData({ ...createFormData, contributionMultiplier: e.target.value })}
                inputProps={{ min: 0, step: 0.5 }}
                helperText={
                  baseAmount
                    ? `Pays ${formatINR(baseAmount * (Number(createFormData.contributionMultiplier) || 0))} per month`
                    : 'Shares of the base monthly amount'
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Password"
                type="password"
                value={createFormData.password}
                onChange={(e) => setCreateFormData({ ...createFormData, password: e.target.value })}
                required
                helperText="Minimum 6 characters"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Confirm Password"
                type="password"
                value={createFormData.confirmPassword}
                onChange={(e) => setCreateFormData({ ...createFormData, confirmPassword: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              {/* Same shape as the status toggle. The select this replaces
                  compared a boolean to the string 'true', so it could only ever
                  store false — an admin could not be created here at all. */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2,
                  p: 1.75,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: (t) => tint(createFormData.isAdmin ? ACCENTS.gold : ACCENTS.silver, 0.08),
                }}
              >
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {createFormData.isAdmin ? 'Administrator' : 'Member'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {createFormData.isAdmin
                      ? 'Can collect payments, edit rules and manage every member'
                      : 'Can view everything and manage their own profile'}
                  </Typography>
                </Box>
                <Switch
                  checked={Boolean(createFormData.isAdmin)}
                  onChange={(e) => setCreateFormData({ ...createFormData, isAdmin: e.target.checked })}
                  inputProps={{ 'aria-label': 'Member is an administrator' }}
                />
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleCreateMember} 
            variant="contained"
            disabled={!createFormData.username || !createFormData.fullName || !createFormData.email || !createFormData.password || createFormData.password !== createFormData.confirmPassword}
          >
            Create Member
          </Button>
        </DialogActions>
      </Dialog>

      {/* Role Change Confirmation Dialog */}
      <Dialog 
        open={roleConfirmDialogOpen} 
        onClose={() => setRoleConfirmDialogOpen(false)} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center">
            {roleChangeTarget?.is_admin ? <PersonRemove color="warning" sx={{ mr: 1 }} /> : <Security color="primary" sx={{ mr: 1 }} />}
            Confirm Role Change
          </Box>
        </DialogTitle>
        <DialogContent>
          {roleChangeTarget && (
            <Box>
              <Alert severity={roleChangeTarget.is_admin ? "warning" : "info"} sx={{ mb: 2 }}>
                <Typography variant="body2">
                  {roleChangeTarget.is_admin ? (
                    <>
                      You are about to <strong>remove admin privileges</strong> from{' '}
                      <strong>{roleChangeTarget.full_name}</strong>. They will become a regular member.
                    </>
                  ) : (
                    <>
                      You are about to <strong>grant admin privileges</strong> to{' '}
                      <strong>{roleChangeTarget.full_name}</strong>. They will be able to manage all aspects of the system.
                    </>
                  )}
                </Typography>
              </Alert>
              
              <Typography variant="body2" color="textSecondary">
                Admin privileges include:
              </Typography>
              <List dense sx={{ mt: 1 }}>
                <ListItem>
                  <ListItemText 
                    primary="• Manage payments and penalties"
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText 
                    primary="• Add, edit, and delete members"
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText 
                    primary="• Access audit logs and reports"
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText 
                    primary="• Configure system settings"
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
              </List>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRoleConfirmDialogOpen(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleRoleToggle} 
            variant="contained" 
            color={roleChangeTarget?.is_admin ? "warning" : "primary"}
            startIcon={roleChangeTarget?.is_admin ? <PersonRemove /> : <Security />}
          >
            {roleChangeTarget?.is_admin ? "Remove Admin Role" : "Grant Admin Role"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Members;